import { h, mount } from '../lib/dom.js';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api.js';
import { renderShell, pageHead, empty, loading } from '../components/shell.js';
import { taskRow, openTaskModal, openBreakModal } from '../components/task.js';
import { renderWeekSpread } from '../components/weekSpread.js';
import { noteCard } from '../components/note.js';
import { renderWeekReport } from '../components/weekReport.js';
import { studentNoteCard, CATEGORY_KEYS, categoryLabel } from '../components/studentNote.js';
import { openModal, closeModal, toast, confirmModal } from '../lib/ui.js';
import { navigate } from '../lib/router.js';
import { weekCard, weekGroup, viewSwitch, groupWeeks, isWeekFinished } from '../components/week.js';
import { fmtDate, fmtDateRange, fmtDuration, dayName, todayIso, visibilityLabel, statusLabel, statusIcon, sourceTypeLabel, sourceTypeIcon } from '../lib/format.js';

// ==== Coach dashboard ====
export async function renderCoachDashboard({ outlet }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const data = await apiGet('/dashboard/coach');
  container.replaceChildren();

  const week = data.week;
  const rep = data.week_report;

  container.appendChild(pageHead(
    'Genel Bakış',
    dayName(data.today) + ', ' + fmtDate(data.today) + ' · Öğrencinin haftası bir bakışta.',
    h('a', { href: '#/coach/weeks', class: 'btn btn-secondary' }, 'Haftaları yönet'),
  ));

  container.appendChild(renderCoachHero(week, rep));

  if (rep) {
    container.appendChild(renderPerSubject(rep.per_subject));
  }

  container.appendChild(h('div', { class: 'grid grid-2', style: { marginTop: 'var(--sp-6)' } },
    renderRecentActivity(data.recent_activity),
    renderRecentWeeks(data.recent_weeks),
  ));
}

function renderCoachHero(week, rep) {
  if (!week) {
    return h('div', { class: 'card' },
      h('div', { class: 'stack stack-sm' },
        h('div', { class: 'eyebrow' }, 'Bu hafta'),
        h('h2', {}, 'Aktif hafta yok'),
        h('p', { class: 'muted' }, 'Bir hafta oluşturarak plan yapmaya başla.'),
        h('a', { class: 'btn btn-primary', href: '#/coach/weeks', style: { alignSelf: 'flex-start', marginTop: 'var(--sp-2)' } }, '+ Yeni hafta'),
      ),
    );
  }
  const s = rep?.summary;
  return h('div', { class: 'card' },
    h('div', { class: 'row-between row-wrap' },
      h('div', {},
        h('div', { class: 'eyebrow' }, 'Bu hafta'),
        h('h2', {}, week.title || fmtDateRange(week.start_date, week.end_date)),
        h('p', { class: 'muted' }, fmtDateRange(week.start_date, week.end_date)),
      ),
      h('a', { class: 'btn btn-secondary', href: '#/coach/weeks/' + week.id }, 'Haftayı aç →'),
    ),
    week.weekly_goal_text ? h('p', { style: { marginTop: 'var(--sp-3)', fontStyle: 'italic' } }, '“' + week.weekly_goal_text + '”') : null,
    s ? h('div', { class: 'grid grid-3', style: { marginTop: 'var(--sp-4)' } },
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Tamamlama'),
        h('span', { class: 'stat-value' }, s.completion_rate + '%'),
        h('span', { class: 'stat-sub' }, s.counts.completed + ' / ' + s.counts.total + ' görev'),
      ),
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Süre'),
        h('span', { class: 'stat-value' }, fmtDuration(s.actual_minutes)),
        h('span', { class: 'stat-sub' }, 'Plan: ' + fmtDuration(s.planned_minutes) + ' · Hedef: ' + fmtDuration(s.target_minutes)),
      ),
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Ertelenen'),
        h('span', { class: 'stat-value' }, s.counts.postponed + ''),
        h('span', { class: 'stat-sub' }, s.most_postponed_subject ? 'En çok: ' + s.most_postponed_subject.name : 'Erteleme yok'),
      ),
    ) : null,
  );
}

function renderPerSubject(perSubject) {
  if (!perSubject || perSubject.length === 0) return h('div');
  return h('section', { style: { marginTop: 'var(--sp-6)' } },
    h('div', { class: 'section-header' }, h('h3', {}, 'Ders bazlı çalışma')),
    h('div', { class: 'table-wrap' },
      h('table', { class: 'table' },
        h('thead', {}, h('tr', {},
          h('th', {}, 'Ders'),
          h('th', {}, 'Görev'),
          h('th', {}, 'Tamamlanan'),
          h('th', {}, 'Ertelenen'),
          h('th', {}, 'Plan Süre'),
          h('th', {}, 'Gerçekleşen'),
        )),
        h('tbody', {}, perSubject.map(s => h('tr', {},
          h('td', {}, h('span', { class: 'subject-swatch', style: { '--sw': s.subject_color || 'var(--color-border-strong)' } }), s.subject_name),
          h('td', {}, s.total + ''),
          h('td', {}, s.completed + ''),
          h('td', { class: s.postponed > 0 ? 'muted' : '' }, s.postponed + ''),
          h('td', {}, fmtDuration(s.planned_minutes)),
          h('td', {}, fmtDuration(s.actual_minutes)),
        ))),
      ),
    ),
  );
}

function renderRecentActivity(items) {
  const box = h('div', { class: 'card' },
    h('h3', {}, 'Son öğrenci aktivitesi'),
  );
  if (!items || items.length === 0) {
    box.appendChild(h('p', { class: 'muted', style: { marginTop: 'var(--sp-3)' } }, 'Henüz aktivite yok.'));
    return box;
  }
  const list = h('div', { class: 'stack', style: { '--stack-gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' } });
  items.forEach(i => {
    list.appendChild(h('div', { class: 'task-row' },
      h('div', { class: 'task-body' },
        h('div', { class: 'task-title' }, i.title),
        h('div', { class: 'task-meta' },
          h('span', { class: `chip chip-status status-${i.status}` },
            h('span', { class: 'status-icon' }, statusIcon(i.status)),
            statusLabel(i.status),
          ),
          i.subject_name ? h('span', { class: 'muted' }, '· ' + i.subject_name) : null,
          i.actual_minutes ? h('span', { class: 'muted' }, '· ' + fmtDuration(i.actual_minutes)) : null,
          h('span', { class: 'subtle' }, '· ' + fmtDate(i.completed_at || i.updated_at)),
        ),
      ),
    ));
  });
  box.appendChild(list);
  return box;
}

function renderRecentWeeks(weeks) {
  const box = h('div', { class: 'card' },
    h('div', { class: 'card-head' },
      h('h3', {}, 'Son haftalar'),
      h('a', { class: 'btn btn-ghost btn-sm', href: '#/coach/weeks' }, 'Tümü →'),
    ),
  );
  if (!weeks || weeks.length === 0) {
    box.appendChild(h('p', { class: 'muted' }, 'Henüz hafta yok.'));
    return box;
  }
  const list = h('div', { class: 'stack', style: { '--stack-gap': 'var(--sp-2)' } });
  weeks.forEach(w => list.appendChild(
    h('a', { href: '#/coach/weeks/' + w.id, class: 'task-row', style: { textDecoration: 'none', color: 'inherit' } },
      h('div', { class: 'task-body' },
        h('div', { class: 'task-title' }, w.title || fmtDateRange(w.start_date, w.end_date)),
        h('div', { class: 'task-meta' },
          h('span', { class: 'muted' }, fmtDateRange(w.start_date, w.end_date)),
          h('span', { class: 'chip' }, w.status),
        ),
      ),
    ),
  ));
  box.appendChild(list);
  return box;
}

// ==== Coach: weeks list & editor ====
export async function renderCoachWeeks({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const view = query?.view || 'active';
  const includeArchived = view === 'archived' ? 1 : 0;
  const { weeks } = await apiGet('/weeks?include_archived=' + includeArchived + '&limit=50');
  container.replaceChildren();

  const refresh = () => renderCoachWeeks({ outlet, query });
  const tabs = viewSwitch('/coach/weeks', view, [
    ['active', 'Aktif'],
    ['completed', 'Tamamlanan'],
    ['archived', 'Arşiv'],
  ]);

  container.appendChild(pageHead(
    view === 'archived' ? 'Arşivlenmiş Haftalar'
      : view === 'completed' ? 'Tamamlanan Haftalar'
      : 'Haftalar',
    view === 'archived' ? 'Arşivden geri aktifleştirebilir ya da tekrar planlayabilirsin.'
      : view === 'completed' ? 'Tüm görevleri tamamlanmış haftalar. Yeni bitene, üste eklenir.'
      : 'Bu haftayı ve yaklaşanları öne çıkarır; geçmiş haftalar aşağıda listelenir.',
    tabs,
    h('button', {
      class: 'btn btn-primary',
      onclick: () => openWeekModal(null, refresh),
    }, '+ Yeni hafta'),
  ));

  if (!weeks || weeks.length === 0) {
    container.appendChild(empty(
      view === 'archived' ? 'Arşivde hafta yok'
        : view === 'completed' ? 'Henüz tamamlanan hafta yok'
        : 'Henüz hafta yok',
      view === 'archived' ? 'Arşivlediğin bir hafta bulunmuyor.'
        : view === 'completed' ? 'Bir haftanın tüm görevleri tamamlandığında burada listelenir.'
        : 'Bir hafta oluşturarak plan yapmaya başla.',
    ));
    return;
  }

  const g = groupWeeks(weeks, todayIso());
  const build = (w, kind) => weekCard(w, {
    kind, refresh, canManage: true, hrefBase: '/coach/weeks',
    onEdit: (week) => openWeekModal(week, refresh),
  });

  const wrap = h('div', { class: 'stack stack-lg' });
  if (view === 'archived') {
    const s = weekGroup('Arşiv', g.archived, 'archived', build);
    if (s) wrap.appendChild(s);
  } else if (view === 'completed') {
    const s = weekGroup('Tamamlanan', g.completed, 'completed', build);
    if (s) wrap.appendChild(s);
    if (g.completed.length === 0) {
      container.appendChild(empty('Henüz tamamlanan hafta yok',
        'Bir haftanın tüm görevleri tamamlandığında burada listelenir.'));
      return;
    }
  } else {
    [
      weekGroup('Bu hafta', g.current, 'current', build),
      weekGroup('Yaklaşan', g.upcoming, 'upcoming', build),
      weekGroup('Geçmiş', g.past, 'past', build),
    ].forEach(s => s && wrap.appendChild(s));
    if (g.current.length + g.upcoming.length + g.past.length === 0) {
      container.appendChild(empty('Aktif hafta yok',
        'Tamamlanan ya da arşivli haftalar için üstteki sekmeleri kullan.'));
      return;
    }
  }
  container.appendChild(wrap);
}

function openWeekModal(week, onDone) {
  const isEdit = !!week;
  const start = h('input', { class: 'input', type: 'date', required: true, value: week?.start_date || suggestStart() });
  const end   = h('input', { class: 'input', type: 'date', required: true, value: week?.end_date   || suggestEnd() });
  // Kullanıcı başlangıç değiştirirse bitişi otomatik +6 güne kaydır (yalnızca yeni oluştururken)
  if (!isEdit) {
    start.addEventListener('change', () => {
      if (!start.value) return;
      const d = new Date(start.value);
      d.setDate(d.getDate() + 6);
      end.value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    });
  }
  const title = h('input', { class: 'input', type: 'text', maxlength: 150, value: week?.title || '', placeholder: 'Örn: Fonksiyonlar Haftası' });
  const goal  = h('textarea', { class: 'textarea', placeholder: 'Bu hafta öğrencinin ana odağı ne?' }, week?.weekly_goal_text || '');
  const hours = h('input', { class: 'input', type: 'number', min: 0, step: 1, value: week?.target_hours || 25 });
  const status = h('select', { class: 'select' },
    h('option', { value: 'draft' }, 'Taslak'),
    h('option', { value: 'published' }, 'Yayında'),
    h('option', { value: 'completed' }, 'Tamamlandı'),
  );
  status.value = week?.status || 'published';

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          start_date: start.value, end_date: end.value,
          title: title.value || null, weekly_goal_text: goal.value || null,
          target_hours: Number(hours.value) || 0, status: status.value,
        };
        if (isEdit) await apiPatch('/weeks/' + week.id, payload);
        else await apiPost('/weeks', payload);
        toast(isEdit ? 'Hafta güncellendi' : 'Hafta oluşturuldu', { type: 'success' });
        closeModal(); onDone && onDone();
      } catch (err) {
        toast(err.message || 'Kaydedilemedi', { type: 'error' });
        save.disabled = false;
      }
    }
  }, isEdit ? 'Kaydet' : 'Haftayı oluştur');

  openModal({
    title: isEdit ? 'Haftayı düzenle' : 'Yeni hafta',
    body: h('div', { class: 'stack' },
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Başlangıç'), start),
        h('div', { class: 'field' }, h('label', {}, 'Bitiş'), end),
        h('div', { class: 'field' }, h('label', {}, 'Hedef (saat)'), hours),
        h('div', { class: 'field' }, h('label', {}, 'Durum'), status),
      ),
      h('div', { class: 'field' }, h('label', {}, 'Başlık'), title),
      h('div', { class: 'field' }, h('label', {}, 'Haftalık hedef'), goal),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      save,
    ],
  });
}

function suggestStart() {
  return todayIso();
}
function suggestEnd() {
  const d = new Date();
  d.setDate(d.getDate() + 6);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// ==== Coach: single week editor ====
export async function renderCoachWeekEditor({ outlet, params }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const wid = params.id;
  const [{ week }, { tasks }] = await Promise.all([
    apiGet('/weeks/' + wid),
    apiGet('/weeks/' + wid + '/tasks'),
  ]);
  container.replaceChildren();

  container.appendChild(pageHead(
    week.title || fmtDateRange(week.start_date, week.end_date),
    fmtDateRange(week.start_date, week.end_date) + (week.weekly_goal_text ? ' · ' + week.weekly_goal_text : ''),
    h('button', { class: 'btn btn-secondary', onclick: () => openWeekModal(week, () => renderCoachWeekEditor({ outlet, params })) }, 'Haftayı düzenle'),
    h('a', { class: 'btn btn-secondary', href: '#/coach/weeks/' + wid + '/report' }, 'Rapor →'),
    h('button', { class: 'btn btn-primary',
      onclick: () => openTaskModal(null, () => renderCoachWeekEditor({ outlet, params }),
        { week_id: Number(wid), task_date: week.start_date })
    }, '+ Yeni görev'),
    h('button', { class: 'btn btn-secondary',
      onclick: () => openBreakModal(null, () => renderCoachWeekEditor({ outlet, params }),
        { week_id: Number(wid), task_date: week.start_date })
    }, '+ Mola'),
    h('button', {
      class: 'btn btn-danger',
      title: 'Haftayı arşivle',
      onclick: () => confirmModal({
        title: 'Haftayı arşivle',
        message: (week.title || fmtDateRange(week.start_date, week.end_date))
          + ' arşivlenecek. Görevler ve geçmiş veriler korunur.',
        confirmText: 'Arşivle', danger: true,
        onConfirm: async () => {
          await apiDelete('/weeks/' + wid);
          toast('Hafta arşivlendi');
          navigate('/coach/weeks');
        },
      }),
    }, 'Arşivle'),
  ));

  container.appendChild(renderWeekSpread(week, tasks, {
    canEdit: true,
    onChange: () => renderCoachWeekEditor({ outlet, params }),
    onAddTask: (date, kind = 'task', extras = {}) => {
      const refresh = () => renderCoachWeekEditor({ outlet, params });
      const defaults = { week_id: Number(wid), task_date: date, ...extras };
      if (kind === 'break') return openBreakModal(null, refresh, defaults);
      return openTaskModal(null, refresh, defaults);
    },
  }));
}

// ==== Coach: report page ====
export async function renderCoachReport({ outlet, params }) {
  return renderWeekReport({
    outlet,
    weekId: params.id,
    backHref: '/coach/weeks/' + params.id,
    audience: 'coach',
  });
}

// ==== Coach: view student notes (read-only) ====
export async function renderCoachStudentNotes({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const category = query?.category || 'all';
  const url = '/student-notes' + (category !== 'all' ? '?category=' + category : '');
  const { notes } = await apiGet(url);
  container.replaceChildren();

  container.appendChild(pageHead(
    'Öğrenci Notları',
    'Öğrencinin yazdığı notları burada okuyabilirsin. Notlar öğrenci tarafından yönetilir.',
  ));

  const catTabs = h('div', { class: 'category-tabs' });
  const catLink = (k, label) => h('a', {
    href: k === 'all' ? '#/coach/student-notes' : '#/coach/student-notes?category=' + k,
    class: 'btn btn-sm ' + (category === k ? 'btn-primary' : 'btn-ghost'),
  }, label);
  catTabs.appendChild(catLink('all', 'Tümü'));
  CATEGORY_KEYS.forEach(k => catTabs.appendChild(catLink(k, categoryLabel(k))));
  container.appendChild(catTabs);

  if (!notes || notes.length === 0) {
    container.appendChild(empty(
      category === 'all' ? 'Henüz not yok' : categoryLabel(category) + ' kategorisinde not yok',
      'Öğrenci not eklediğinde burada göreceksin.',
    ));
    return;
  }
  const list = h('div', { class: 'stack' });
  notes.forEach(n => list.appendChild(studentNoteCard(n, { canManage: false })));
  container.appendChild(list);
}

// ==== Coach: subjects & topics — master-detail layout ====
const TOPIC_STATUSES = [
  { v: 'not_started',  label: 'Başlanmadı',     cls: 'pending' },
  { v: 'in_progress',  label: 'Çalışılıyor',    cls: 'in_progress' },
  { v: 'needs_review', label: 'Tekrar gerekli', cls: 'in_progress' },
  { v: 'learned',      label: 'Öğrenildi',      cls: 'completed' },
  { v: 'completed',    label: 'Tamamlandı',     cls: 'completed' },
];

export async function renderCoachSubjects({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const [{ subjects }, { topics }] = await Promise.all([
    apiGet('/subjects'), apiGet('/topics'),
  ]);
  container.replaceChildren();
  const refresh = () => renderCoachSubjects({ outlet, query });

  container.appendChild(pageHead(
    'Dersler & Konular',
    'Solda dersleri seç, sağda konuları hızlıca yönet.',
    h('button', { class: 'btn btn-primary',
      onclick: () => openSubjectModal(null, refresh) },
      '+ Yeni ders'),
  ));

  if (subjects.length === 0) {
    container.appendChild(empty('Henüz ders yok', 'Sağ üstteki "+ Yeni ders" ile başla.'));
    return;
  }

  const bySubject = new Map(subjects.map(s => [s.id, []]));
  topics.forEach(t => { if (bySubject.has(t.subject_id)) bySubject.get(t.subject_id).push(t); });

  // Seçili ders — query'den al ya da ilkine düş
  let selectedId = Number(query?.subject) || subjects[0].id;
  if (!subjects.some(s => s.id === selectedId)) selectedId = subjects[0].id;

  // Layout
  const layout = h('div', { class: 'subjects-layout' });
  const rail = h('aside', { class: 'subjects-rail' });
  const pane = h('section', { class: 'subjects-pane' });
  layout.appendChild(rail);
  layout.appendChild(pane);
  container.appendChild(layout);

  // === Sol kılavuz ===
  subjects.forEach(s => {
    const count = (bySubject.get(s.id) || []).length;
    const isSel = s.id === selectedId;
    rail.appendChild(h('a', {
      href: '#/coach/subjects?subject=' + s.id,
      class: 'subject-rail-item' + (isSel ? ' active' : ''),
    },
      h('span', { class: 'subject-swatch', style: { '--sw': s.color || 'var(--color-border-strong)' } }),
      h('span', { class: 'srail-name' }, s.name),
      h('span', { class: 'srail-count' }, count + ''),
    ));
  });

  // === Sağ panel: seçili dersin konuları ===
  const selected = subjects.find(s => s.id === selectedId);
  const topicList = bySubject.get(selectedId) || [];

  // Header
  pane.appendChild(h('div', { class: 'pane-head' },
    h('div', { class: 'row' },
      h('span', { class: 'subject-swatch subject-swatch-lg',
        style: { '--sw': selected.color || 'var(--color-border-strong)' } }),
      h('h2', { style: { margin: 0 } }, selected.name),
      h('span', { class: 'muted' }, topicList.length + ' konu'),
    ),
    h('div', { class: 'row' },
      h('button', { class: 'btn btn-secondary btn-sm',
        onclick: () => openBulkTopicModal(selected, refresh) }, 'Toplu ekle'),
      h('button', { class: 'btn btn-ghost btn-sm',
        onclick: () => openSubjectModal(selected, refresh) }, 'Dersi düzenle'),
      h('button', { class: 'btn btn-danger btn-sm',
        onclick: () => confirmModal({
          title: 'Dersi arşivle',
          message: '"' + selected.name + '" arşivlenecek. Geçmiş görevler etkilenmez.',
          confirmText: 'Arşivle', danger: true,
          onConfirm: async () => { await apiDelete('/subjects/' + selected.id); toast('Arşivlendi'); refresh(); },
        }),
      }, 'Arşivle'),
    ),
  ));

  // Quick-add satırı
  const qInput = h('input', { class: 'input', type: 'text', maxlength: 150,
    placeholder: 'Konu ekle — Enter ile hızlı ekleyebilirsin' });
  const qAdd = async () => {
    const name = qInput.value.trim();
    if (!name) return;
    qInput.disabled = true;
    try {
      await apiPost('/topics', { subject_id: selected.id, name, sort_order: topicList.length });
      toast('Konu eklendi', { type: 'success' });
      refresh();
    } catch (err) { toast(err.message, { type: 'error' }); qInput.disabled = false; qInput.focus(); }
  };
  qInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); qAdd(); }});
  pane.appendChild(h('div', { class: 'topic-quick-add' },
    qInput,
    h('button', { class: 'btn btn-primary', onclick: qAdd }, 'Ekle'),
  ));

  // Konu arama
  const searchIn = h('input', { class: 'input topic-search', type: 'search',
    placeholder: 'Konularda ara…' });
  pane.appendChild(h('div', { class: 'topic-search-row' }, searchIn));

  // Konu tablosu
  const tableWrap = h('div', { class: 'topic-table' });
  pane.appendChild(tableWrap);

  const renderTopics = () => {
    tableWrap.replaceChildren();
    const q = searchIn.value.trim().toLowerCase();
    const filtered = q ? topicList.filter(t => t.name.toLowerCase().includes(q)) : topicList;
    if (filtered.length === 0) {
      tableWrap.appendChild(h('div', { class: 'empty', style: { padding: 'var(--sp-6) var(--sp-4)' } },
        h('h4', {}, q ? '"' + q + '" bulunamadı' : 'Bu derste henüz konu yok'),
        h('p', { class: 'subtle' }, q
          ? 'Farklı bir arama dene.'
          : 'Yukarıdaki alandan hızlıca ekleyebilirsin.'),
      ));
      return;
    }
    filtered.forEach(t => tableWrap.appendChild(renderTopicRow(t, selected, refresh)));
  };
  searchIn.addEventListener('input', renderTopics);
  renderTopics();
}

function renderTopicRow(t, subject, refresh) {
  const nameInput = h('input', { class: 'topic-name-input', type: 'text', value: t.name, maxlength: 150 });
  const commitName = async () => {
    const newName = nameInput.value.trim();
    if (!newName || newName === t.name) { nameInput.value = t.name; return; }
    try {
      await apiPatch('/topics/' + t.id, { name: newName });
      t.name = newName;
      toast('Kaydedildi', { type: 'success' });
    } catch (err) { toast(err.message, { type: 'error' }); nameInput.value = t.name; }
  };
  nameInput.addEventListener('blur', commitName);
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); nameInput.blur(); }
    if (e.key === 'Escape') { nameInput.value = t.name; nameInput.blur(); }
  });

  const statusSel = h('select', { class: 'select select-inline' },
    ...TOPIC_STATUSES.map(s => h('option', { value: s.v }, s.label)),
  );
  statusSel.value = t.status || 'not_started';
  statusSel.addEventListener('change', async () => {
    try {
      await apiPatch('/topics/' + t.id, { status: statusSel.value });
      // Auto-sync progress at extremes
      if (statusSel.value === 'not_started' && (t.progress_percent | 0) > 0) {
        progRange.value = 0; progText.textContent = '0%'; progBar.style.width = '0%';
        await apiPatch('/topics/' + t.id, { progress_percent: 0 });
      } else if ((statusSel.value === 'learned' || statusSel.value === 'completed')
                 && (t.progress_percent | 0) < 100) {
        progRange.value = 100; progText.textContent = '100%'; progBar.style.width = '100%';
        await apiPatch('/topics/' + t.id, { progress_percent: 100 });
      }
      t.status = statusSel.value;
      row.className = 'topic-row status-' + statusClass(statusSel.value);
      toast('Durum güncellendi', { type: 'success' });
    } catch (err) { toast(err.message, { type: 'error' }); }
  });

  const progRange = h('input', { class: 'progress-range', type: 'range', min: 0, max: 100, step: 5,
    value: t.progress_percent ?? 0 });
  const progText = h('span', { class: 'progress-value' }, (t.progress_percent ?? 0) + '%');
  const progBar = h('div', { class: 'progress-bar', style: { width: (t.progress_percent ?? 0) + '%' } });
  const progressCell = h('div', { class: 'progress-cell' },
    h('div', { class: 'progress' }, progBar),
    h('div', { class: 'progress-controls' }, progRange, progText),
  );
  let saveTimer = null;
  progRange.addEventListener('input', () => {
    progBar.style.width = progRange.value + '%';
    progText.textContent = progRange.value + '%';
  });
  progRange.addEventListener('change', () => {
    const val = Number(progRange.value);
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      try {
        await apiPatch('/topics/' + t.id, { progress_percent: val });
        t.progress_percent = val;
      } catch (err) { toast(err.message, { type: 'error' }); }
    }, 200);
  });

  const row = h('div', { class: 'topic-row status-' + statusClass(t.status) },
    h('div', { class: 'topic-name-cell' }, nameInput),
    h('div', { class: 'topic-status-cell' }, statusSel),
    progressCell,
    h('div', { class: 'topic-actions-cell' },
      h('button', {
        class: 'btn btn-danger btn-sm', title: 'Konuyu arşivle',
        onclick: () => confirmModal({
          title: 'Konuyu arşivle',
          message: '"' + t.name + '" arşivlenecek. Geçmiş görevler etkilenmez.',
          confirmText: 'Arşivle', danger: true,
          onConfirm: async () => { await apiDelete('/topics/' + t.id); toast('Arşivlendi'); refresh(); },
        }),
      }, '✕'),
    ),
  );
  return row;
}

function statusClass(s) {
  return ({
    not_started: 'pending', in_progress: 'in_progress',
    learned: 'completed', needs_review: 'in_progress', completed: 'completed'
  })[s] || 'pending';
}

function openBulkTopicModal(subject, onDone) {
  const ta = h('textarea', { class: 'textarea', required: true,
    style: { minHeight: '220px' },
    placeholder: 'Her satıra bir konu adı yaz:\nFonksiyonlar\nPolinomlar\nTrigonometri\n…' });
  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      const lines = ta.value.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) { toast('En az bir satır yaz', { type: 'error' }); return; }
      save.disabled = true;
      let ok = 0, fail = 0;
      for (let i = 0; i < lines.length; i++) {
        try {
          await apiPost('/topics', { subject_id: subject.id, name: lines[i], sort_order: i });
          ok++;
        } catch { fail++; }
      }
      toast(ok + ' konu eklendi' + (fail ? ` · ${fail} hata` : ''),
        { type: fail ? 'error' : 'success' });
      closeModal();
      onDone && onDone();
    },
  }, 'Ekle');
  openModal({
    title: 'Toplu konu ekle · ' + subject.name,
    body: h('div', { class: 'stack' },
      h('p', { class: 'muted', style: { fontSize: 'var(--fs-small)' } },
        'Her satır ayrı bir konu olarak eklenir. Boş satırlar ve baş/son boşluklar yoksayılır.'),
      ta,
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      save,
    ],
  });
}

function openSubjectModal(subject, onDone) {
  const isEdit = !!subject;
  const name = h('input', { class: 'input', type: 'text', maxlength: 100, required: true, value: subject?.name || '' });
  const color = h('input', { class: 'input', type: 'color', value: subject?.color || '#2F5D50' });
  const sort = h('input', { class: 'input', type: 'number', min: 0, value: subject?.sort_order ?? 0 });
  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = { name: name.value.trim(), color: color.value, sort_order: Number(sort.value) };
        if (isEdit) await apiPatch('/subjects/' + subject.id, payload);
        else await apiPost('/subjects', payload);
        toast(isEdit ? 'Ders güncellendi' : 'Ders eklendi', { type: 'success' });
        closeModal(); onDone && onDone();
      } catch (err) { toast(err.message, { type: 'error' }); save.disabled = false; }
    } }, isEdit ? 'Kaydet' : 'Ekle');
  openModal({
    title: isEdit ? 'Dersi düzenle' : 'Yeni ders',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Ad'), name),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Renk'), color),
        h('div', { class: 'field' }, h('label', {}, 'Sıra'), sort),
      ),
    ),
    actions: [h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'), save],
  });
}

function openTopicModal(topic, subject, onDone) {
  const isEdit = !!topic;
  const name = h('input', { class: 'input', type: 'text', maxlength: 150, required: true, value: topic?.name || '' });
  const sort = h('input', { class: 'input', type: 'number', min: 0, value: topic?.sort_order ?? 0 });
  const status = h('select', { class: 'select' },
    h('option', { value: 'not_started' }, 'Başlanmadı'),
    h('option', { value: 'in_progress' }, 'Çalışılıyor'),
    h('option', { value: 'needs_review' }, 'Tekrar gerekli'),
    h('option', { value: 'learned' }, 'Öğrenildi'),
    h('option', { value: 'completed' }, 'Tamamlandı'),
  );
  status.value = topic?.status || 'not_started';
  const prog = h('input', { class: 'input', type: 'number', min: 0, max: 100, value: topic?.progress_percent ?? 0 });
  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          name: name.value.trim(), sort_order: Number(sort.value),
          status: status.value, progress_percent: Number(prog.value),
        };
        if (isEdit) await apiPatch('/topics/' + topic.id, payload);
        else await apiPost('/topics', { ...payload, subject_id: subject.id });
        toast(isEdit ? 'Konu güncellendi' : 'Konu eklendi', { type: 'success' });
        closeModal(); onDone && onDone();
      } catch (err) { toast(err.message, { type: 'error' }); save.disabled = false; }
    } }, isEdit ? 'Kaydet' : 'Ekle');
  openModal({
    title: (isEdit ? 'Konuyu düzenle' : 'Yeni konu') + ' · ' + subject.name,
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Ad'), name),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Sıra'), sort),
        h('div', { class: 'field' }, h('label', {}, 'Durum'), status),
        h('div', { class: 'field' }, h('label', {}, 'İlerleme %'), prog),
      ),
    ),
    actions: [h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'), save],
  });
}

// ==== Coach: notes ====
export async function renderCoachNotes({ outlet }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const { notes } = await apiGet('/coach-notes');
  container.replaceChildren();
  container.appendChild(pageHead(
    'Öğrenci Notları',
    'Öğrenciye görünür ya da yalnızca senin göreceğin notlar.',
    h('button', { class: 'btn btn-primary', onclick: () => openNoteModal(null, () => renderCoachNotes({ outlet })) }, '+ Yeni not'),
  ));
  if (notes.length === 0) {
    container.appendChild(empty('Henüz not yok', 'Öğrenci için bir yönlendirme yazarak başla.'));
    return;
  }
  const refresh = () => renderCoachNotes({ outlet });
  const list = h('div', { class: 'stack' });
  notes.forEach(n => list.appendChild(noteCard(n, {
    canManage: true,
    showVisibility: true,
    onEdit: (note) => openNoteModal(note, refresh),
    refresh,
  })));
  container.appendChild(list);
}

function openNoteModal(note, onDone) {
  const isEdit = !!note;
  const title = h('input', { class: 'input', type: 'text', maxlength: 200, value: note?.title || '', placeholder: 'Başlık (opsiyonel)' });
  const content = h('textarea', { class: 'textarea', required: true, style: { minHeight: '140px' } }, note?.content || '');
  const date = h('input', { class: 'input', type: 'date', required: true, value: note?.note_date || todayIso() });
  const tag = h('input', { class: 'input', type: 'text', maxlength: 50, value: note?.tag || '', placeholder: 'örn: tekrar' });
  const vis = h('select', { class: 'select' },
    h('option', { value: 'visible_to_student' }, 'Öğrenciye görünür'),
    h('option', { value: 'coach_only' }, 'Yalnızca koç'),
  );
  vis.value = note?.visibility || 'visible_to_student';
  const subjSel = h('select', { class: 'select' }, h('option', { value: '' }, '— Ders (opsiyonel) —'));
  apiGet('/subjects').then(({ subjects }) => {
    subjects.forEach(s => subjSel.appendChild(h('option', { value: s.id }, s.name)));
    subjSel.value = note?.subject_id || '';
  });

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          title: title.value || null, content: content.value.trim(),
          note_date: date.value, tag: tag.value || null,
          visibility: vis.value, subject_id: subjSel.value ? Number(subjSel.value) : null,
        };
        if (isEdit) await apiPatch('/coach-notes/' + note.id, payload);
        else await apiPost('/coach-notes', payload);
        toast(isEdit ? 'Güncellendi' : 'Eklendi', { type: 'success' });
        closeModal(); onDone && onDone();
      } catch (err) { toast(err.message, { type: 'error' }); save.disabled = false; }
    }
  }, isEdit ? 'Kaydet' : 'Notu ekle');

  openModal({
    title: isEdit ? 'Notu düzenle' : 'Yeni not',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Başlık'), title),
      h('div', { class: 'field' }, h('label', {}, 'İçerik'), content),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Tarih'), date),
        h('div', { class: 'field' }, h('label', {}, 'Görünürlük'), vis),
        h('div', { class: 'field' }, h('label', {}, 'Ders'), subjSel),
        h('div', { class: 'field' }, h('label', {}, 'Etiket'), tag),
      ),
    ),
    actions: [h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'), save],
  });
}

// ==== Coach: sources CRUD ====
const SOURCE_TYPES = [
  { v: 'all',          label: 'Tümü' },
  { v: 'video',        label: 'Video' },
  { v: 'question_bank',label: 'Soru Bankası' },
  { v: 'book',         label: 'Kitap' },
  { v: 'other',        label: 'Diğer' },
];

export async function renderCoachSources({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const filter = query?.type || 'all';
  const url = '/sources' + (filter !== 'all' ? '?type=' + filter : '');
  const { sources } = await apiGet(url);
  container.replaceChildren();
  const refresh = () => renderCoachSources({ outlet, query });

  container.appendChild(pageHead(
    'Kaynaklar',
    'Video kanalları, soru bankaları, kitaplar. Görev eklerken buradan seçilir.',
    h('button', { class: 'btn btn-primary',
      onclick: () => openSourceModal(null, filter === 'all' ? 'other' : filter, refresh),
    }, '+ Yeni kaynak'),
  ));

  const tabs = h('div', { class: 'category-tabs' });
  SOURCE_TYPES.forEach(t => tabs.appendChild(h('a', {
    href: t.v === 'all' ? '#/coach/sources' : '#/coach/sources?type=' + t.v,
    class: 'btn btn-sm ' + (filter === t.v ? 'btn-primary' : 'btn-ghost'),
  }, t.label)));
  container.appendChild(tabs);

  if (!sources || sources.length === 0) {
    container.appendChild(empty(
      filter === 'all' ? 'Henüz kaynak yok' : sourceTypeLabel(filter) + ' türünde kaynak yok',
      '"+ Yeni kaynak" ile başla.',
    ));
    return;
  }

  const list = h('div', { class: 'stack' });
  sources.forEach(s => list.appendChild(renderSourceCard(s, refresh)));
  container.appendChild(list);
}

function renderSourceCard(s, refresh) {
  return h('div', { class: 'note-card', style: { borderLeftColor: 'var(--color-accent)' } },
    h('div', { class: 'row row-wrap', style: { alignItems: 'baseline' } },
      h('span', { class: 'chip' }, sourceTypeIcon(s.type), ' ', sourceTypeLabel(s.type)),
      h('h4', { style: { margin: 0 } }, s.name),
      s.publisher ? h('span', { class: 'muted' }, '· ' + s.publisher) : null,
      s.url ? h('a', { class: 'muted', href: s.url, target: '_blank', rel: 'noopener' }, '· Bağlantı') : null,
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-ghost btn-sm', onclick: () => openSourceModal(s, s.type, refresh) }, 'Düzenle'),
      h('button', { class: 'btn btn-danger btn-sm',
        onclick: () => confirmModal({
          title: 'Kaynağı arşivle',
          message: '"' + s.name + '" arşivlenecek. Geçmiş görevlerdeki bağlantı korunur.',
          confirmText: 'Arşivle', danger: true,
          onConfirm: async () => { await apiDelete('/sources/' + s.id); toast('Arşivlendi'); refresh(); },
        }),
      }, '✕'),
    ),
    s.notes ? h('p', { class: 'note-content', style: { marginTop: 'var(--sp-2)' } }, s.notes) : null,
  );
}

function openSourceModal(source, defaultType, onDone) {
  const isEdit = !!source;
  const name = h('input', { class: 'input', type: 'text', maxlength: 150, required: true, value: source?.name || '' });
  const typeSel = h('select', { class: 'select' },
    h('option', { value: 'video' }, 'Video'),
    h('option', { value: 'question_bank' }, 'Soru Bankası'),
    h('option', { value: 'book' }, 'Kitap'),
    h('option', { value: 'other' }, 'Diğer'),
  );
  typeSel.value = source?.type || defaultType || 'other';
  const publisher = h('input', { class: 'input', type: 'text', maxlength: 120, value: source?.publisher || '', placeholder: 'Kanal / yayınevi' });
  const url = h('input', { class: 'input', type: 'url', maxlength: 500, value: source?.url || '', placeholder: 'https://…' });
  const notes = h('textarea', { class: 'textarea', placeholder: 'Not (opsiyonel)' }, source?.notes || '');

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          name: name.value.trim(), type: typeSel.value,
          publisher: publisher.value || null, url: url.value || null, notes: notes.value || null,
        };
        if (isEdit) await apiPatch('/sources/' + source.id, payload);
        else await apiPost('/sources', payload);
        toast(isEdit ? 'Güncellendi' : 'Eklendi', { type: 'success' });
        closeModal(); onDone && onDone();
      } catch (err) { toast(err.message || 'Kaydedilemedi', { type: 'error' }); save.disabled = false; }
    },
  }, isEdit ? 'Kaydet' : 'Ekle');

  openModal({
    title: isEdit ? 'Kaynağı düzenle' : 'Yeni kaynak',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Ad'), name),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Tür'), typeSel),
        h('div', { class: 'field' }, h('label', {}, 'Kanal / yayınevi'), publisher),
      ),
      h('div', { class: 'field' }, h('label', {}, 'Bağlantı'), url),
      h('div', { class: 'field' }, h('label', {}, 'Not'), notes),
    ),
    actions: [h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'), save],
  });
}

// ==== Coach: source & topic reports ====
function ymdDaysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export async function renderCoachReports({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  const view = query?.view || 'sources';
  const from = query?.from || ymdDaysAgo(30);
  const to   = query?.to   || todayIso();

  const mkTab = (v, label) => {
    const q = new URLSearchParams({ view: v, from, to });
    return h('a', {
      href: '#/coach/reports?' + q.toString(),
      class: 'btn btn-sm ' + (view === v ? 'btn-primary' : 'btn-ghost'),
      'aria-current': view === v ? 'page' : null,
    }, label);
  };
  const tabs = h('div', { class: 'view-switch' },
    mkTab('sources', 'Kaynak bazlı'),
    mkTab('topics',  'Konu bazlı'),
  );

  const fromIn = h('input', { class: 'input', type: 'date', value: from });
  const toIn   = h('input', { class: 'input', type: 'date', value: to });
  const applyBtn = h('button', { class: 'btn btn-secondary btn-sm',
    onclick: () => {
      const q = new URLSearchParams({ view, from: fromIn.value, to: toIn.value });
      navigate('/coach/reports?' + q.toString());
    },
  }, 'Uygula');
  const resetBtn = h('button', { class: 'btn btn-ghost btn-sm',
    onclick: () => navigate('/coach/reports?view=' + view),
  }, 'Sıfırla');

  container.appendChild(pageHead(
    'Rapor',
    'Kaynak ve konu bazlı toplam istatistikler (D − 0.25·Y net dahil).',
    tabs,
  ));
  container.appendChild(h('div', { class: 'row row-wrap', style: { alignItems: 'flex-end', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' } },
    h('div', { class: 'field' }, h('label', {}, 'Başlangıç'), fromIn),
    h('div', { class: 'field' }, h('label', {}, 'Bitiş'), toIn),
    applyBtn, resetBtn,
  ));

  const body = h('div');
  container.appendChild(body);
  body.appendChild(loading());
  const url = '/reports/' + view + '?from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to);
  const { rows } = await apiGet(url);
  body.replaceChildren();

  if (!rows || rows.length === 0) {
    body.appendChild(empty('Bu aralıkta veri yok', 'Tarih aralığını genişletmeyi dene.'));
    return;
  }
  if (view === 'sources') body.appendChild(renderSourceReportTable(rows));
  else body.appendChild(renderTopicReportTable(rows));
}

function renderSourceReportTable(rows) {
  return h('div', { class: 'table-wrap' },
    h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Kaynak'),
        h('th', {}, 'Tür'),
        h('th', {}, 'Oturum'),
        h('th', {}, 'Süre'),
        h('th', {}, 'Soru'),
        h('th', {}, 'D'),
        h('th', {}, 'Y'),
        h('th', {}, 'B'),
        h('th', {}, 'Net'),
      )),
      h('tbody', {}, rows.map(r => h('tr', {},
        h('td', {}, r.name + (r.publisher ? ' — ' + r.publisher : '')),
        h('td', {}, sourceTypeLabel(r.type)),
        h('td', {}, r.sessions + ''),
        h('td', {}, fmtDuration(Number(r.minutes) || 0)),
        h('td', {}, r.question_actual + ''),
        h('td', {}, r.correct + ''),
        h('td', {}, r.incorrect + ''),
        h('td', {}, (r.blank || 0) + ''),
        h('td', {}, r.net + ''),
      ))),
    ),
  );
}

function renderTopicReportTable(rows) {
  return h('div', { class: 'table-wrap' },
    h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Ders'),
        h('th', {}, 'Konu'),
        h('th', {}, 'Oturum'),
        h('th', {}, 'Süre'),
        h('th', {}, 'Soru'),
        h('th', {}, 'D'),
        h('th', {}, 'Y'),
        h('th', {}, 'B'),
        h('th', {}, 'Net'),
      )),
      h('tbody', {}, rows.map(r => h('tr', {},
        h('td', {}, h('span', { class: 'subject-swatch', style: { '--sw': r.subject_color || 'var(--color-border-strong)' } }), r.subject_name || '—'),
        h('td', {}, r.topic_name),
        h('td', {}, r.sessions + ''),
        h('td', {}, fmtDuration(Number(r.minutes) || 0)),
        h('td', {}, r.question_actual + ''),
        h('td', {}, r.correct + ''),
        h('td', {}, r.incorrect + ''),
        h('td', {}, (r.blank || 0) + ''),
        h('td', {}, r.net + ''),
      ))),
    ),
  );
}
